import * as React from 'react';
import { Alert, Box, Close } from '@apia/theme';
import { CSSTransition } from 'react-transition-group';
import { getIndex, useUnmount, useUpdateEffect } from '@apia/util';
import { Icon, isIconName, TIconName } from '@apia/icons';
import { INotification } from './types';
import {
  defaultNotifier,
  onCloseNotificationCallbacks,
} from './defaultNotifier';
import Trace from './Trace';

export interface INotificationProps {
  animationName?: string;
  animationTimeout?: number;
  className?: string;
  disableClose?: boolean;
  notification: INotification;
}

export const Notification: React.FC<INotificationProps> = ({
  animationName = 'fromRight',
  animationTimeout = 150,
  className,
  disableClose,
  notification,
}) => {
  const innerNotification = React.useMemo(
    () => ({ ...notification }),
    [notification],
  );
  const nodeRef = React.useRef(null);
  if (!innerNotification.title)
    innerNotification.title = getIndex(
      [window.LBL_ERROR, window.LBL_WARNING, window.LBL_COMPLETE_OPERATION],
      [
        innerNotification.type === 'danger',
        innerNotification.type === 'warning',
        innerNotification.type === 'success',
      ],
    );
  if (!innerNotification.icon)
    innerNotification.icon = getIndex<TIconName>(
      ['Check', 'Alert', 'Close'],
      [
        innerNotification.type === 'success',
        innerNotification.type === 'warning',
        innerNotification.type === 'danger',
      ],
    );

  const [shouldShow, setShouldShow] = React.useState(true);

  const close = React.useCallback(() => {
    defaultNotifier.delete(innerNotification);
  }, [innerNotification]);

  useUpdateEffect(() => {
    if (!innerNotification.isOpen) setShouldShow(false);
  }, [innerNotification.isOpen]);

  useUnmount(() => {
    if (onCloseNotificationCallbacks[innerNotification.id])
      onCloseNotificationCallbacks[innerNotification.id]();
  });

  let notificationType: INotification['type'] = 'warning';
  if (['sysException', 'exception', 'danger'].includes(innerNotification.type))
    notificationType = 'danger';
  if (['greenMessage', 'success'].includes(innerNotification.type))
    notificationType = 'success';
  if (['info'].includes(innerNotification.type)) notificationType = 'info';

  const onClick = React.useCallback(() => {
    setShouldShow(false);
    defaultNotifier.close(innerNotification);
  }, [innerNotification]);

  return (
    <CSSTransition
      in={shouldShow}
      timeout={animationTimeout}
      classNames={animationName}
      appear
      unmountOnExit
      onExited={close}
      nodeRef={nodeRef}
    >
      <Alert
        data-testid={notificationType}
        role="alert"
        ref={nodeRef}
        variant={`alerts.${notificationType}`}
        className={`${
          className ?? ''
        } notification notification__alert ${notificationType}`}
        tabIndex={0}
        onKeyDown={React.useCallback((ev: React.KeyboardEvent) => {
          if (
            ev.key.toLowerCase() === 'escape' ||
            ev.key.toLowerCase() === 'enter' ||
            ev.key === ' '
          ) {
            ev.preventDefault();
            ev.stopPropagation();
            setShouldShow(false);
          }
        }, [])}
      >
        {(!disableClose ||
          innerNotification.icon ||
          innerNotification.title) && (
          <Box className="notification__header">
            {(innerNotification.title || innerNotification.icon) && (
              <Box as="h4" className="notification__title">
                {innerNotification.icon && (
                  <Box className="notification__icon">
                    {isIconName(
                      innerNotification.icon as unknown as TIconName,
                    ) ? (
                      <Icon
                        title=""
                        name={innerNotification.icon as unknown as TIconName}
                      />
                    ) : (
                      innerNotification.icon
                    )}
                  </Box>
                )}
                {innerNotification.title}
              </Box>
            )}
            {!disableClose && (
              <Close
                type="button"
                onClick={onClick}
                tabIndex={-1}
                className="notification__closeButton"
              />
            )}
          </Box>
        )}
        <Box className="notification__body">
          <Box className="notification__content">
            <Box className="notification__message">
              <Box
                dangerouslySetInnerHTML={{
                  __html: innerNotification.message,
                }}
                className="notification__text"
              />
            </Box>
            {innerNotification.trace && (
              <Trace trace={innerNotification.trace} />
            )}
          </Box>
        </Box>
      </Alert>
    </CSSTransition>
  );
};
